<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { BODY_RAW_TYPES, BODY_TYPES, RequestSection } from '@/utils/apiTab'
import { useApiTabStore } from '@/stores/apiTab.store'
import BodyFormDataTab from '@/components/ide/ide/tabs/ApiTabComp/BodyTab/BodyFormDataTab.vue'
import BodyFormUrlEncodedTab from '@/components/ide/ide/tabs/ApiTabComp/BodyTab/BodyFormUrlEncodedTab.vue'
import BodyRawTab from '@/components/ide/ide/tabs/ApiTabComp/BodyTab/BodyRawTab.vue'
import BaseSelect from '@/components/forms/BaseSelect.vue'
import { FORM_SELECT_VALUE_TYPE } from '@/components/forms/form.enum'

const props = defineProps({
  activeRequestIndex: {
    type: Number,
    required: true
  }
})

const apiTabStore = useApiTabStore()

const bodyTypes = Object.values(BODY_TYPES)
const bodyRawTypes = Object.values(BODY_RAW_TYPES)

const selectedBodyType = ref<string>(BODY_TYPES.FORM_DATA)
const bodyRawContentType = ref<string>(BODY_RAW_TYPES.TEXT)

/**
 * Updates the raw content type in the bodyRawContentType state.
 * @param type - The new raw content type to be set.
 */
const handleUpdateRawContentType = (type: string) => {
  bodyRawContentType.value = type
}
/**
 * Retrieves and sets the body type based on the active request's stored body tab data.
 * If the body tab data exists, it sets the body type to the stored authorization data.
 * Otherwise, it defaults to `BODY_TYPES.FORM_DATA`.
 */
const retrieveValuesFromStore = () => {
  const basisBodyTabData = apiTabStore.getRequestFieldData(
    props.activeRequestIndex,
    RequestSection.BODY,
    null
  )
  if (basisBodyTabData) {
    selectedBodyType.value = basisBodyTabData as BODY_TYPES
  } else {
    selectedBodyType.value = BODY_TYPES.FORM_DATA
  }
}

onMounted(() => {
  retrieveValuesFromStore()
})

watch(
  () => props.activeRequestIndex,
  () => {
    retrieveValuesFromStore()
  }
)

watch(
  () => selectedBodyType.value,
  () => {
    apiTabStore.updateRequestField(
      props.activeRequestIndex,
      RequestSection.BODY,
      selectedBodyType.value,
      null
    )
  }
)
</script>

<template>
  <div class="mx-auto flex w-full max-w-full flex-col gap-4">
    <div
      class="flex h-10 w-full items-start justify-start gap-2"
      :class="selectedBodyType == BODY_TYPES.RAW_DATA ? 'flex-row' : 'flex-col'"
    >
      <div class="w-full">
        <BaseSelect
          :list="bodyTypes"
          :valueType="FORM_SELECT_VALUE_TYPE.NAME"
          v-model="selectedBodyType"
          customClass="text-high block h-9 w-full appearance-none rounded-md background-primary tracking-tight !p-2 outline-none !border-border-primary"
          :showLable="false"
          :getSelectedByItem="true"
          :isLightGray="false"
        />
      </div>
      <div class="w-full">
        <BaseSelect
          v-if="selectedBodyType == BODY_TYPES.RAW_DATA"
          :list="bodyRawTypes"
          :valueType="FORM_SELECT_VALUE_TYPE.NAME"
          v-model="bodyRawContentType"
          customClass="text-high block h-9 w-full appearance-none rounded-md background-primary tracking-tight !p-2 outline-none !border-border-primary"
          :showLable="false"
          :getSelectedByItem="true"
          :isLightGray="false"
        />
      </div>
    </div>

    <div class="flex h-full min-h-fit w-full items-center justify-center">
      <div
        class="flex h-full w-full flex-col gap-4"
        v-if="selectedBodyType == BODY_TYPES.FORM_DATA"
      >
        <BodyFormDataTab :activeRequestIndex="props.activeRequestIndex" />
      </div>
      <div
        class="flex h-full w-full flex-col gap-4"
        v-if="selectedBodyType == BODY_TYPES.FORM_URLENCODED"
      >
        <BodyFormUrlEncodedTab :activeRequestIndex="props.activeRequestIndex" />
      </div>
      <div class="flex h-full w-full flex-col" v-if="selectedBodyType == BODY_TYPES.RAW_DATA">
        <BodyRawTab
          :activeRequestIndex="props.activeRequestIndex"
          :bodyRawContentType="bodyRawContentType"
          :handleUpdateRawContentType="handleUpdateRawContentType"
        />
      </div>
    </div>
  </div>
</template>
